import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2,
  Typography,
  IconButton,
} from '@mui/material';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import levelsBg3 from '../assets/ball-trio-levels.png';
import goldBlurLevels from '../assets/gold-blur-levels.png';
import clearDrops from '../assets/clear-drops.png';

interface Props {
  openKeyDialog: boolean;
  onClose: () => void;
}

interface Row {
  experienceText: string;
  levelNumber: number;
  levelText: string;
  aspirationText: string;
}

const rows: Row[] = [
  {
    experienceText: 'Not experienced in the subject',
    levelNumber: 0,
    levelText: 'N/A',
    aspirationText: 'Not interested in the subject',
  },
  {
    experienceText:
      'Familiar with the basics, and able to solve subject-related problems with team or mentor assistance',
    levelNumber: 1,
    levelText: 'Novice',
    aspirationText: 'Familiar with the basics, and would like to learn more',
  },
  {
    experienceText:
      'Adept in the subject, and can independently apply the knowledge with minimal team or mentor assistance',
    levelNumber: 2,
    levelText: 'Intermediate',
    aspirationText:
      'Familiar with the subject, and am interested in learning more and gaining more experience',
  },
  {
    experienceText:
      'Familiar enough with the subject to independently apply the knowledge in solving problems on a project',
    levelNumber: 3,
    levelText: 'Advanced',
    aspirationText:
      'Very familiar the subject, and would like to gain deeper learnings and ultimately become an expert ',
  },
  {
    experienceText:
      'Proficient in the subject, often the leader in applying the knowledge, and able to direct others accordingly',
    levelNumber: 4,
    levelText: 'Expert',
    aspirationText:
      'Proficient in the subject, and would like to make this a focus area',
  },
  {
    experienceText:
      'Considered a veteran in the subject, capable of identifying needs when presented with a problem, able to talk/write at length on the subject, and qualified to teach others',
    levelNumber: 5,
    levelText: 'Master',
    aspirationText:
      'Considered a veteran in the subject, and would like to teach the subject to others',
  },
];

const tableRow = ({
  experienceText,
  levelNumber,
  levelText,
  aspirationText,
}: Row) => {
  return (
    <Grid2
      container
      marginTop={3}
      marginBottom={1}
      key={levelNumber}
      columnSpacing={8}
    >
      <Grid2
        size={{ xs: 5 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'end',
        }}
      >
        <Typography variant='body1' textAlign='end'>
          {experienceText}
        </Typography>
      </Grid2>
      <Grid2
        size={{ xs: 2 }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>{levelNumber}</Typography>
        <Typography sx={{ fontWeight: 700 }}>{levelText}</Typography>
        <Typography sx={{ fontWeight: 700 }}>{levelNumber}</Typography>
      </Grid2>
      <Grid2
        size={{ xs: 5 }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography variant='body1'>{aspirationText}</Typography>
      </Grid2>
    </Grid2>
  );
};

const headerRow = () => {
  return (
    <React.Fragment>
      <Grid2 size={{ xs: 5 }}>
        <Typography variant='h5' gutterBottom>
          <b>Experience</b> level
        </Typography>
        <Typography variant='subtitle2' paddingBottom={1}>
          What is your amount of experience and understanding?
        </Typography>
        <Box sx={{ borderBottom: 2, borderColor: '#0085D0' }}></Box>
      </Grid2>
      <Grid2
        size={{ xs: 2 }}
        textAlign='center'
        alignSelf='end'
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
        }}
      >
        <Typography
          variant='overline'
          fontSize={16}
          sx={{ fontWeight: 600 }}
          paddingTop={4}
        >
          levels
        </Typography>
      </Grid2>
      <Grid2 size={{ xs: 5 }}>
        <Typography variant='h5' gutterBottom>
          <b>Aspiration</b> to learn and do
        </Typography>
        <Typography variant='subtitle2' paddingBottom={1}>
          What is your level of interest in doing this in the future?
        </Typography>
        <Box sx={{ borderBottom: 2, borderColor: '#0085D0' }}></Box>
      </Grid2>
    </React.Fragment>
  );
};

export const KeyResultsDialog: React.FC<Props> = ({
  openKeyDialog,
  onClose,
}) => {
  return (
    <Dialog
      open={openKeyDialog}
      onClose={onClose}
      maxWidth='xl'
      fullWidth={true}
      PaperProps={{
        style: {
          backgroundColor: '#ECF8FF',
          borderTop: '10px',
          borderTopStyle: 'solid',
          borderTopColor: '#0085D0',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        variant='h4'
        sx={{
          m: 0,
          p: 2,
          fontWeight: 700,
          paddingLeft: '84px',
          paddingRight: '84px',
          paddingTop: '48px',
          marginBottom: '12px',
        }}
        id='customized-dialog-title'
      >
        Levels of experience and aspiration key
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={onClose}
        sx={{
          position: 'absolute',
          left: 8,
          top: 8,
          color: (theme) => theme.palette.grey[900],
        }}
      >
        <CloseIcon fontSize='large' />
      </IconButton>
      <Box
        sx={{
          position: 'absolute',
          bottom: -54,
          right: 8,
          width: '96px',
          height: '132px',
          backgroundImage: `url(${levelsBg3})`,
          backgroundSize: 'cover',
        }}
      ></Box>
      <Box
        sx={{
          position: 'absolute',
          top: 192,
          width: '96px',
          height: '214px',
          backgroundImage: `url(${goldBlurLevels})`,
          backgroundSize: 'cover',
        }}
      ></Box>
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          right: 0,
          width: '6%',
          height: '130px',
          backgroundImage: `url(${clearDrops})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
        }}
      ></Box>
      <DialogContent
        sx={{
          paddingLeft: '76px',
          paddingRight: '76px',
          paddingBottom: '48px',
        }}
      >
        <Grid2 container paddingLeft={2} paddingRight={2}>
          <Grid2 container columnSpacing={8}>
            {headerRow()}
          </Grid2>
        </Grid2>
        <Grid2 container paddingLeft={2} paddingRight={2}>
          {rows.map(
            ({ experienceText, levelNumber, levelText, aspirationText }) =>
              tableRow({
                experienceText,
                levelNumber,
                levelText,
                aspirationText,
              })
          )}
        </Grid2>
        <Grid2 container paddingLeft={2} paddingRight={2} marginY={2}>
          <Grid2 container columnSpacing={8}>
            <Grid2 size={{ xs: 5 }}>
              <Box sx={{ borderBottom: 2, borderColor: '#0085D0' }}></Box>
            </Grid2>
            <Grid2
              size={{ xs: 2 }}
              textAlign='center'
              alignSelf='end'
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
              }}
            ></Grid2>
            <Grid2 size={{ xs: 5 }}>
              <Box sx={{ borderBottom: 2, borderColor: '#0085D0' }}></Box>
            </Grid2>
          </Grid2>
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};
